import Grid from '@mui/material/Grid';
import { useContext } from 'react';
import { useForm } from 'react-hook-form';

import { getSectionVisibilityMessage, hasInitiallyAppliedSettingsCheck } from './helpers';
import { ProfileSectionAccordion } from './ProfileSectionAccordion';
import { useProfileVisibilityFormController } from './ProfileVisibilityFormController';
import { ResetConfigurationButton } from './ResetConfigurationButton';
import { ApplicationContext } from '../../../../../../shared/source/context/context';

import type {
    SectionVisibilityBase,
    UserProfileVisibilityResponse,
    UserProfileVisibilityTemplateResponse,
} from '../../../../../../shared/source/data-contract/profile-visibility';
import type { SectionVisibilityResponse } from '../../../../../../shared/source/data-contract/profile-visibility';
import type { CSSProperties, FunctionComponent } from 'react';

export type ProfileSectionVisibilityKey = 'overallVisibility';

const formStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
};

type Section = {
    readonly appliedSettings: SectionVisibilityResponse;
    readonly label: string;
    readonly minimumSettings: SectionVisibilityBase;
    readonly name: ProfileSectionVisibilityKey;
    readonly rolesList: readonly string[];
};

type ProfileVisibilityFormProps = {
    readonly data: UserProfileVisibilityResponse;
    readonly refetch: () => void;
    readonly rolesList: readonly string[];
    readonly template: UserProfileVisibilityTemplateResponse;
};

export const ProfileVisibilitySimplifiedForm: FunctionComponent<ProfileVisibilityFormProps> = ({
    data,
    refetch,
    rolesList,
    template,
}) => {
    const { translate } = useContext(ApplicationContext);

    const {
        formState: { dirtyFields },
        getValues,
        setValue,
        watch,
    } = useForm<UserProfileVisibilityResponse>({
        values: data,
    });

    const enforceTemplate = (data: SectionVisibilityResponse): SectionVisibilityResponse => {
        const minimum = template.minimum.overallVisibility;
        const everyone = data.everyone || minimum.everyone;
        const roles = everyone ? [] : [...new Set([...data.roles, ...minimum.roles])];
        const users = everyone ? [] : data.users;

        return {
            everyone,
            roles,
            users,
        };
    };

    const setSectionValue = (sectionKey: ProfileSectionVisibilityKey, data: SectionVisibilityResponse): void => {
        setValue(sectionKey, enforceTemplate(data), { shouldDirty: true });
    };

    const getSectionValue = (sectionKey: ProfileSectionVisibilityKey): SectionVisibilityResponse =>
        getValues(sectionKey);

    const { onEveryoneClick, onResetConfiguration, onResetSection, onRoleClick, onSaveSection, onUserClick } =
        useProfileVisibilityFormController({
            getSectionValue,
            refetch,
            setSectionValue,
        });

    const hasInitiallyAppliedSettings = hasInitiallyAppliedSettingsCheck(data, template.minimum);

    const watchedOverallVisibility = watch('overallVisibility');

    const sections: readonly Section[] = [
        {
            appliedSettings: watchedOverallVisibility,
            label: translate('pages.user-profile.profile-visibility.drawer.apply-settings-to.overall-visibility-label'),
            minimumSettings: template.minimum.overallVisibility,
            name: 'overallVisibility',
            rolesList,
        },
    ];

    return (
        <form style={formStyles} onSubmit={(event) => event.preventDefault()}>
            <Grid>
                {sections.map(({ appliedSettings, label, minimumSettings, name, rolesList }) => {
                    const { everyone, roles, users } = data[name];
                    const isSectionDirty = Boolean(dirtyFields[name]);

                    return (
                        <ProfileSectionAccordion
                            key={name}
                            appliedSettings={appliedSettings}
                            isCheckBoxEnabled={false}
                            isSectionDirty={isSectionDirty}
                            minimumSettings={minimumSettings}
                            rolesList={rolesList}
                            sectionLabel={label}
                            visibilityMessage={translate(
                                getSectionVisibilityMessage({
                                    everyone,
                                    roles,
                                    users,
                                }),
                            )}
                            onEveryoneClick={onEveryoneClick(name)}
                            onResetSection={onResetSection(name)}
                            onRoleClick={onRoleClick(name)}
                            onSaveSection={onSaveSection(name)}
                            onUserClick={onUserClick(name)}
                        />
                    );
                })}
            </Grid>
            <Grid alignItems="center" container={true} justifyContent="space-between" minHeight={64} paddingX={3}>
                <ResetConfigurationButton
                    disabled={hasInitiallyAppliedSettings}
                    onResetConfiguration={onResetConfiguration}
                />
            </Grid>
        </form>
    );
};
